<template>
  <div>
    <!--  Summary
    -------------------------------------------------- -->
    <template v-if="isSummary">
      <div class="columns">
        <div class="column">
          <summary-item
            :amount="maxAllowableOffer"
          >Maximum<br>Allowed Offer</summary-item>
        </div>
        <div class="column">
          <summary-item
            :amount="record.projected_profit"
          >Projected<br>Profit</summary-item>
        </div>
      </div>

      <info-item
        label="Closed Date"
        :value="buyingInfo.buying_info_closing_at"
        is-date
      />
    </template>

    <!-- Expanded
    -------------------------------------------------- -->
    <template v-else>
      <info-heading heading="Buying Analysis">
        <div class="flex flex-centered">
          <button-icon
            icon="edit"
            color="is-secondary"
            @click="$refs.novationAnalysisModal.open(analysis)"
          />
        </div>
      </info-heading>

      <div v-if="analysis"> <!-- Need this div to allow css last-of-type to work -->
        <analysis-item label="After Repair Value" :value="analysis.after_repair_value"/>
        <analysis-item label="Repair Costs" :value="analysis.repair_costs"/>
        <analysis-item label="Holding Costs" :value="analysis.holding_costs" :time="analysis.holding_time"/>
        <analysis-item label="Other Costs" :value="analysis.other_costs"/>
        <analysis-item label="Desired Profit" :value="analysis.desired_profit"/>
        <analysis-item label="Maximum Allowed Offer" :value="maxAllowableOffer" is-result/>
      </div>
      <hr>

      <profit-item
        type="Projected"
        :amount="record.projected_profit"
        hint="Project Profit = ARV - All Costs - Latest Buying Offer"
      />
      <hr>

      <!-- Dates -->
      <info-heading
        heading="Dates"
      >
        <button-icon
          icon="edit"
          color="is-secondary"
          @click="$refs.buyingInfoModal.open(buyingInfo)"
        />
      </info-heading>

      <info-item
        label="Accepted Date"
        :value="buyingInfo.buying_info_accepted_at"
        is-date
      />
      <info-item
        label="Contract Date"
        :value="buyingInfo.buying_info_contracted_at"
        is-date
      />
      <info-item
        label="Closed Date"
        :value="buyingInfo.buying_info_closing_at"
        is-date
      />
    </template>

    <modal-container>
      <!-- Buying Info Modal -->
      <modal-crud-form
        ref="buyingInfoModal"
        title="Buying Info Dates"
        form-component="buying-info-form"
        hide-delete-button
        is-triple-column
      />

      <!-- Analysis Modal -->
      <modal-crud-form
        ref="novationAnalysisModal"
        title="Analysis"
        form-component="novation-analysis-form"
        hide-delete-button
      />
    </modal-container>
  </div>
</template>

<script>
import InfoHeading from "./InfoHeading.vue";
import ProfitItem from "./ProfitItem.vue";
import InfoItem from "./InfoItem.vue";
import AnalysisItem from "./AnalysisItem.vue";
import SummaryItem from "./SummaryItem.vue";

export default {
  name: "BuyingInfoNovationAnalysis",
  components: { SummaryItem, AnalysisItem, InfoItem, ProfitItem, InfoHeading },
  props: {
    isSummary: {
      type: Boolean,
      default: false
    }
  },
  computed: {
    record() {
      return this.$store.state.activeRecord.record;
    },
    buyingInfo() {
      return {
        id: this.record.id,
        record_id: this.record.id,
        buying_info_status: this.record.buying_info_status,

        buying_analysis_id: this.record.buying_analysis_id,

        buying_info_accepted_at: this.record.buying_info_accepted_at,
        buying_info_closing_at: this.record.buying_info_closing_at,
        buying_info_contracted_at: this.record.buying_info_contracted_at,
      }
    },
    analysis() {
      return this.$store.state.activeRecord.novationAnalysis;
    },
    worth() {
      if(this.analysis) {
        // these are null by default instead of 0 so the UI is easier to use, but for calculations I need them to be 0
        let afterRepairValue = this.analysis.after_repair_value ? this.analysis.after_repair_value : 0;
        let repairCosts = this.analysis.repair_costs ? this.analysis.repair_costs : 0;
        let otherCosts = this.analysis.other_costs ? this.analysis.other_costs : 0;
        let holdingCosts = this.analysis.holding_costs ? this.analysis.holding_costs : 0;
        let holdingTime = this.analysis.holding_time ? this.analysis.holding_time : 0;

        const holdingCostsCalculated = parseFloat(holdingCosts) * parseFloat(holdingTime);
        const totalCosts = parseFloat(repairCosts) + holdingCostsCalculated + parseFloat(otherCosts);

        return parseFloat(afterRepairValue) - totalCosts;
      }

      return 0;
    },
    maxAllowableOffer() {
      if(this.analysis) {
        let desiredProfit = this.analysis.desired_profit ? this.analysis.desired_profit : 0;

        return this.worth - parseFloat(desiredProfit);
      }

      return 0;
    },
  },
};
</script>

<style scoped lang="scss">
@import "../../../styles/variables/variables";

hr {
  margin: 0.75rem (-$modalGutter);
}
</style>
